import React, { useEffect, useState, useMemo } from "react";
import { Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Alert } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { RepositoryFactory } from "../../../repositories";
import TableContainer from "../../../components/Common/DataTableContainer";

const UnidadPage = () => {
    document.title = "Unidades | GraphicForce - Printesco";

    const [unidades, setUnidades] = useState([]);
    const [unidadesInactivas, setUnidadesInactivas] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [modalAdd, setModalAdd] = useState(false);
    const [form, setForm] = useState({ nombre: "" });
    const [saving, setSaving] = useState(false);
    const [modalError, setModalError] = useState("");
    const [showDeleteAlert, setShowDeleteAlert] = useState(false);
    const [unidadToDelete, setUnidadToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    
    // For restore confirmation
    const [showRestoreAlert, setShowRestoreAlert] = useState(false);
    const [unidadToRestore, setUnidadToRestore] = useState(null);
    const [restoring, setRestoring] = useState(false);

    // Estado de paginación
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    // Get repository instance
    const unidadRepository = useMemo(() => RepositoryFactory.getRepository('unidad'), []);

    // Fetch todas las unidades y separarlas por estado
    const fetchUnidades = async () => {
        try {
            // Obtener todas las unidades de una vez (sin filtro de activo)
            const resultList = await unidadRepository.getList(1, 1000, {
                sort: "-created"
            });
            
            // Separar unidades activas e inactivas
            const activas = resultList.items.filter(unidad => unidad.activo !== false);
            const inactivas = resultList.items.filter(unidad => unidad.activo === false);
            
            // Aplicar paginación solo a las activas
            const startIndex = pageIndex * pageSize;
            const endIndex = startIndex + pageSize;
            const activasPaginadas = activas.slice(startIndex, endIndex);
            
            setUnidades(activasPaginadas);
            setUnidadesInactivas(inactivas);
            setTotalRecords(activas.length);
        } catch (err) {
            console.error("Error fetching unidades:", err);
            setModalError("Error al cargar las unidades");
        }
    };

    useEffect(() => {
        fetchUnidades();
    }, [pageIndex, pageSize]);

    const reloadUnidades = () => {
        fetchUnidades();
    };

    // Handlers
    const handleAdd = () => {
        setForm({ nombre: "" });
        setModalError("");
        setModalAdd(true);
    };

    const handleRemove = (unidad) => {
        setUnidadToDelete(unidad);
        setShowDeleteAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRemove = async () => {
        if (!unidadToDelete) return;
        setDeleting(true);
        try {
            await unidadRepository.deactivate(unidadToDelete.id);
            fetchUnidades();
            setShowDeleteAlert(false);
            setUnidadToDelete(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al desactivar unidad");
        }
        setDeleting(false);
    };

    const cancelRemove = () => {
        setShowDeleteAlert(false);
        setUnidadToDelete(null);
    };

    // Show alert and store unidad to restore
    const handleRestore = (unidad) => {
        setUnidadToRestore(unidad);
        setShowRestoreAlert(true);
        // Hacer scroll hacia arriba para mostrar el alert
        setTimeout(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 100);
    };

    const confirmRestore = async () => {
        if (!unidadToRestore) return;
        setRestoring(true);
        try {
            await unidadRepository.activate(unidadToRestore.id);
            fetchUnidades();
            setShowRestoreAlert(false);
            setUnidadToRestore(null);
            setModalError("");
        } catch (err) {
            setModalError("Error al restaurar unidad");
        }
        setRestoring(false);
    };

    const cancelRestore = () => {
        setShowRestoreAlert(false);
        setUnidadToRestore(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.nombre.trim()) {
            setModalError("El nombre es requerido");
            return;
        }

        setSaving(true);
        try {
            await unidadRepository.createUnidad({
                nombre: form.nombre.trim().toUpperCase(),
                activo: true,
            });
            setModalAdd(false);
            setForm({ nombre: "" });
            setModalError("");
            fetchUnidades();
        } catch (err) {
            console.error("Error creating unidad:", err);
            setModalError("Error al crear la unidad");
        }
        setSaving(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({
            ...prev,
            [name]: value
        }));
        // Clear error when user starts typing
        if (modalError) {
            setModalError("");
        }
    };

    // Columnas para la tabla de unidades activas
    const columns = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Activo",
            accessorKey: "activo",
            cell: info =>
                info.getValue() === false
                    ? <span className="badge bg-danger">No</span>
                    : <span className="badge bg-success">Sí</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const unidad = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="danger" size="sm" onClick={() => handleRemove(unidad)} disabled={unidad.activo === false}>
                            <i className="fas fa-eraser me-1"></i> Eliminar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [unidades]);

    // Columnas para la tabla de unidades inactivas
    const columnsInactivas = useMemo(() => [
        {
            header: "Nombre",
            accessorKey: "nombre",
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Estado",
            accessorKey: "activo",
            cell: info =>
                <span className="badge bg-danger">Inactivo</span>,
            enableColumnFilter: false,
            enableSorting: true,
        },
        {
            header: "Acciones",
            accessorKey: "acciones",
            cell: info => {
                const unidad = info.row.original;
                return (
                    <div style={{ display: "flex", gap: 4, minWidth: 140, maxWidth: 180 }}>
                        <Button color="success" size="sm" onClick={() => handleRestore(unidad)}>
                            <i className="fas fa-undo me-1"></i> Restaurar
                        </Button>
                    </div>
                );
            },
            enableColumnFilter: false,
            enableSorting: false,
            meta: { style: { width: "1%", whiteSpace: "nowrap" } },
        },
    ], [unidadesInactivas]);

    return (
        <React.Fragment>
            <div className="page-content">
                <div className="container-fluid">
                    <Breadcrumbs maintitle="Dashboard" title="Inventario Materiales" breadcrumbItem="Unidades" />

                    {/* Dismissing Alert for Delete */}
                    {showDeleteAlert && unidadToDelete && (
                        <Alert
                            color="warning"
                            isOpen={showDeleteAlert}
                            toggle={cancelRemove}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Está seguro?</strong> Esto desactivará la unidad <b>{unidadToDelete.nombre}</b>.
                            </div>
                            <div>
                                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                                    {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    {/* Dismissing Alert for Restore */}
                    {showRestoreAlert && unidadToRestore && (
                        <Alert
                            color="info"
                            isOpen={showRestoreAlert}
                            toggle={cancelRestore}
                            fade={true}
                            transition={{ timeout: 150 }}
                            className="d-flex align-items-center justify-content-between"
                        >
                            <div>
                                <strong>¿Restaurar unidad?</strong> Esto reactivará la unidad <b>{unidadToRestore.nombre}</b>.
                            </div>
                            <div>
                                <Button color="success" size="sm" className="me-2" onClick={confirmRestore} disabled={restoring}>
                                    {restoring ? <Spinner size="sm" /> : <i className="fas fa-undo me-1"></i>} Restaurar
                                </Button>
                                <Button color="secondary" size="sm" onClick={cancelRestore} disabled={restoring}>
                                    Cancelar
                                </Button>
                            </div>
                        </Alert>
                    )}

                    <Row>
                        <Col>
                            <Card>
                                <CardBody>
                                    <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                        Unidades
                                        <div>
                                            <Button color="primary" size="sm" onClick={handleAdd}>
                                                <i className="fas fa-plus me-1"></i> Agregar
                                            </Button>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                className="ms-2"
                                                title="Recargar tabla"
                                                onClick={reloadUnidades}
                                            >
                                                <i className="fas fa-sync-alt"></i>
                                            </Button>
                                        </div>
                                    </CardTitle>
                                    <div className="table-rep-plugin">
                                        <div className="table-responsive mb-0" data-pattern="priority-columns">
                                            <TableContainer
                                                columns={columns}
                                                data={unidades}
                                                isGlobalFilter={false}
                                                isPagination={true}
                                                isCustomPageSize={true}
                                                pageIndex={pageIndex}
                                                setPageIndex={setPageIndex}
                                                pageSize={pageSize}
                                                setPageSize={setPageSize}
                                                totalRecords={totalRecords}
                                                pagination="pagination"
                                                paginationWrapper='dataTables_paginate paging_simple_numbers'
                                                tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                            />
                                        </div>
                                    </div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>

                        {/* Tabla de Unidades Inactivas */}
                        <Row className="mt-4">
                            <Col>
                                <Card>
                                    <CardBody>
                                        <CardTitle className="h4 d-flex justify-content-between align-items-center">
                                            Unidades Inactivas
                                            <div>
                                                <Button
                                                    color="secondary"
                                                    size="sm"
                                                    title="Recargar tabla"
                                                    onClick={reloadUnidades}
                                                >
                                                    <i className="fas fa-sync-alt"></i>
                                                </Button>
                                            </div>
                                        </CardTitle>
                                        <div className="table-rep-plugin">
                                            <div className="table-responsive mb-0" data-pattern="priority-columns">
                                                <TableContainer
                                                    columns={columnsInactivas}
                                                    data={unidadesInactivas}
                                                    isGlobalFilter={false}
                                                    isPagination={false}
                                                    isCustomPageSize={false}
                                                    tableClass="table-bordered table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
                                                />
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            </Col>
                        </Row>

                    {/* Modal para agregar unidad */}
                    <Modal isOpen={modalAdd} toggle={() => setModalAdd(!modalAdd)} size="lg">
                        <ModalHeader toggle={() => setModalAdd(!modalAdd)}>Agregar Unidad</ModalHeader>
                        <Form onSubmit={handleSubmit}>
                            <ModalBody>
                                {modalError && (
                                    <Alert color="danger">
                                        {modalError}
                                    </Alert>
                                )}
                                <FormGroup>
                                    <Label for="nombre">Nombre de la Unidad *</Label>
                                    <Input
                                        type="text"
                                        name="nombre"
                                        id="nombre"
                                        value={form.nombre}
                                        onChange={handleInputChange}
                                        placeholder="Ej: Kilogramo, Metro, Litro"
                                        required
                                    />
                                </FormGroup>
                            </ModalBody>
                            <ModalFooter>
                                <Button color="secondary" onClick={() => setModalAdd(false)}>
                                    Cancelar
                                </Button>
                                <Button color="primary" type="submit" disabled={saving}>
                                    {saving ? <Spinner size="sm" className="me-2" /> : null}
                                    Guardar
                                </Button>
                            </ModalFooter>
                        </Form>
                    </Modal>
                </div>
            </div>
        </React.Fragment>
    );
};

export default UnidadPage;
